/** @format */

import {styled, Theme} from '@mui/material/styles';
import {Box, Button, Typography} from '@mui/material';

import {SimpleButton} from 'components/buttons/SimpleButton';

import {ButtonVariantProps, HighlightProps, HotspotVariant, VariantProps} from '../../types';
import {getButtonColor} from '../../helpers/getButtonColor';

export const Root = styled(Box)(({theme}) => ({
  width: 'max-content',
  height: '356px',
  background: theme.palette.semantic_dark?.surface.surface1,
  position: 'relative',
}));

export const Content = styled(Box)(({theme}) => ({
  height: '100%',
  padding: theme.spacing(4),
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'flex-end',
  gap: theme.spacing(4),
  position: 'relative',
  zIndex: 10,
}));

export const HighlightBg = styled(Box)(({theme}) => ({
  width: '100%',
  height: '100%',
  position: 'absolute',
  top: 0,
  left: 0,
  zIndex: 0,
  background: theme.palette.semantic_dark?.primary.container,
  opacity: 0.5,
}));

export const Header = styled(Box)(({theme}) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(2),
}));

export const HeaderText = styled(Typography)(({theme}) => ({
  color: theme.palette.semantic_dark?.surface.onSurface,
  fontSize: '24px',
  fontWeight: 400,
  lineHeight: '28px',
}));

export const HeaderWithIcon = styled(Box)(({theme}) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(4),

  '.MuiSvgIcon-root': {
    fontSize: '28px',
  },
}));

export const ChipsColumns = styled(Box)(({theme}) => ({
  display: 'flex',
  gap: theme.spacing(33),
}));

export const Chips = styled(Box)(({theme}) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(0.5),
}));

export const Chip = styled(Box, {
  shouldForwardProp: prop => prop !== 'isHighlight',
})<HighlightProps>(({theme, isHighlight}) => ({
  borderRadius: '18px',
  background: isHighlight
    ? theme.palette.semantic_dark?.primary.container
    : theme.palette.semantic_dark?.surface.surface2,
  height: '36px',
  width: 'max-content',
  padding: theme.spacing(0, 4),
  display: 'flex',
  alignItems: 'center',

  color: theme.palette.semantic_dark?.surface.onSurface,
  fontSize: '16px',
  fontWeight: 500,
  lineHeight: '20px',
}));

export const DescriptionBox = styled(Box)(({theme}) => ({
  maxWidth: '470px',
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(2),
}));

export const DescriptionText = styled(Typography)(({theme}) => ({
  color: theme.palette.semantic_dark?.surface.onSurface,
  fontSize: '16px',
  fontWeight: 400,
  lineHeight: '20px',
}));

export const ButtonsBox = styled(Box, {
  shouldForwardProp: prop => prop !== 'variant',
})<VariantProps>(({theme, variant}) => ({
  display: 'flex',
  border: `1px solid ${getButtonColor(variant, theme)}`,
  borderRadius: '8px',
  overflow: 'hidden',
}));

const getHideButtonTextColor = (variant: HotspotVariant, theme: Theme): string | undefined => {
  switch (variant) {
    case 'blue':
      return theme.palette.semantic_dark?.primary.main;
    case 'orange':
      return theme.palette.semantic_dark?.alert.main;
    default:
      return theme.palette.semantic_dark?.surface.onSurface;
  }
};

const getHideButtonHoverColor = (variant: HotspotVariant, theme: Theme): string | undefined => {
  switch (variant) {
    case 'blue':
      return `${theme.palette.semantic_dark?.primary.hover}33`;
    case 'orange':
      return `${theme.palette.semantic_dark?.alert.main}33`;
    default:
      return `${theme.palette.semantic_dark?.surface.onSurfaceMuted}33`;
  }
};

export const HideButton = styled(Button, {
  shouldForwardProp: prop => prop !== 'customVariant',
})<ButtonVariantProps>(({theme, customVariant}) => ({
  height: '32px',
  padding: theme.spacing(0, 4),
  borderRadius: 0,

  color: getHideButtonTextColor(customVariant, theme),
  fontSize: '14px',
  fontWeight: 400,
  lineHeight: '16px',

  '&:hover': {
    background: getHideButtonHoverColor(customVariant, theme),
  },
}));

export const AlertTooltip = styled(Box)(({theme}) => ({
  background: theme.palette.semantic_dark?.surface.inverseSurface,
  padding: theme.spacing(4),
  borderRadius: '8px',
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(4),
  width: '250px',
}));

export const AlertTooltipButtonsBox = styled(Box)(() => ({
  display: 'flex',
  borderRadius: '8px',
  overflow: 'hidden',
}));

export const SimpleButtonWrap = styled(SimpleButton)(({theme}) => ({
  padding: theme.spacing(0, 4),
  borderRadius: 0,
  flexShrink: 0,
}));
